import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { CheckCircle, Sparkles, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const CreditSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, API } = useContext(AuthContext); // Using API from AuthContext
  const [verifying, setVerifying] = useState(true);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (token) {
      verifyPurchase();
    }
  }, [searchParams, token]);

  const verifyPurchase = async () => {
    const sessionId = searchParams.get('session_id');
    const paymentId = searchParams.get('paymentId');
    const payerId = searchParams.get('PayerID');
    const orderId = searchParams.get('order_id');
    const provider = searchParams.get('provider');

    // If no verifiable params, stop
    if (!sessionId && !paymentId && !orderId) {
      setVerifying(false);
      return;
    }

    try {
      let response;
      if (provider === 'paypal' && paymentId && payerId) {
        response = await axios.post(`${API}/credits/verify/paypal`,
          { paymentId, PayerID: payerId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else if (provider === 'cashfree' && orderId) {
        response = await axios.post(`${API}/credits/verify/cashfree`,
          { order_id: orderId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else if (sessionId) {
        // Default stripe
        response = await axios.get(
          `${API}/credits/purchase/verify/${sessionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      if (response && response.data.success) {
        setResult(response.data);
        if (!response.data.already_processed) {
          toast.success("Credits added successfully!");
        }
      }
    } catch (err) {
      toast.error('Failed to verify purchase');
      console.error(err);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen hero-background flex items-center justify-center p-4">
      <Card className="bg-gray-900 border-gray-800 w-full max-w-md">
        <CardContent className="p-8 text-center">
          {verifying ? (
            <>
              <Loader2 className="w-16 h-16 text-purple-500 animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">Verifying Purchase...</h2>
              <p className="text-gray-400">Please wait while we confirm your payment.</p>
            </>
          ) : result?.success ? (
            <>
              <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Payment Successful!</h2>
              <p className="text-gray-400 mb-6">
                {result.already_processed
                  ? 'Your credits were already added to your account.'
                  : result.credits_added
                    ? `${result.credits_added} credits have been added to your account.`
                    : 'Credits have been added to your account.'
                }
              </p>
              {result.total_credits !== undefined && (
                <div className="glass-card p-4 rounded-xl mb-6 flex items-center justify-center gap-3">
                  <Sparkles className="w-6 h-6 text-yellow-400" />
                  <span className="text-2xl font-bold text-white">{result.total_credits}</span>
                  <span className="text-gray-400">total credits</span>
                </div>
              )}
              <div className="flex gap-3">
                <Button
                  onClick={() => navigate('/create-comic-flow')}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-700 hover:to-cyan-600"
                >
                  Start Creating
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  onClick={() => navigate('/credits')}
                  variant="outline"
                  className="border-gray-700 text-gray-300"
                >
                  View Credits
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-10 h-10 text-yellow-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Verification Issue</h2>
              <p className="text-gray-400 mb-6">We couldn't verify your purchase. If you were charged, please contact support.</p>
              <Button onClick={() => navigate('/credits')} className="bg-purple-600 hover:bg-purple-700">
                Back to Credits
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CreditSuccess;
